/**
 * ExecutionSelector - Component for browsing and selecting pipeline executions
 * Provides real data browsing for Demo Viewer instead of hardcoded execution IDs
 */

import { useState, useEffect } from 'react';
import { ChevronDown, Clock, AlertCircle, CheckCircle, Play, Loader } from 'lucide-react';
import { useExecutions, type PipelineExecution } from '@/hooks/useExecutions';
import { cn } from '@/utils/cn';

interface ExecutionSelectorProps {
  executionId?: string; // Allow both executionId and selectedExecutionId for compatibility
  selectedExecutionId?: string;
  onExecutionChange: (executionId: string) => void;
  onLog?: (logEntry: any) => void; // Optional logging callback
  className?: string;
  'data-testid'?: string;
}

export function ExecutionSelector({
  executionId,
  selectedExecutionId,
  onExecutionChange,
  onLog: _onLog, // Mark as unused with underscore prefix
  className,
  'data-testid': testId
}: ExecutionSelectorProps) {
  // Support both prop names for compatibility
  const currentExecutionId = executionId || selectedExecutionId;
  const [isOpen, setIsOpen] = useState(false);

  const { data, isLoading, error } = useExecutions({
    limit: 50,
    withOutputCounts: true
  });

  const executions = data?.executions || [];
  const selectedExecution = executions.find(exec => exec.id === currentExecutionId);

  // Auto-select first execution with outputs if none selected or current doesn't exist
  useEffect(() => {
    if (!isLoading && executions.length > 0) {
      // If no execution selected or selected execution doesn't exist in current list
      if (!currentExecutionId || !selectedExecution) {
        // Find first execution with outputs
        const executionWithOutputs = executions.find(exec => (exec.output_count || 0) > 0);
        if (executionWithOutputs && executionWithOutputs.id !== currentExecutionId) {
          onExecutionChange(executionWithOutputs.id);
        }
      }
    }
  }, [executions, currentExecutionId, selectedExecution, isLoading, onExecutionChange]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-400" />;
      case 'running':
        return <Loader className="w-4 h-4 text-blue-400 animate-spin" />;
      case 'initialized':
        return <Play className="w-4 h-4 text-gray-400" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-400 bg-green-400/10 border-green-400/30';
      case 'failed':
        return 'text-red-400 bg-red-400/10 border-red-400/30';
      case 'running':
        return 'text-blue-400 bg-blue-400/10 border-blue-400/30';
      case 'initialized':
        return 'text-gray-400 bg-gray-400/10 border-gray-400/30';
      default:
        return 'text-gray-400 bg-gray-400/10 border-gray-400/30';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatExecutionName = (execution: PipelineExecution) => {
    const date = formatDate(execution.created_at);
    const stage = execution.current_stage || 'unknown';
    const outputCount = execution.output_count || 0;

    return {
      primary: `${execution.id.slice(0, 8)}... (${stage})`,
      secondary: `${date} • ${outputCount} outputs`
    };
  };

  if (error) {
    return (
      <div className="p-3 border border-red-500/30 rounded-lg bg-red-500/10">
        <div className="flex items-center gap-2 text-red-400 text-sm">
          <AlertCircle className="w-4 h-4" />
          Failed to load executions: {error.message}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("relative", className)} data-testid={testId}>
      <div className="space-y-2">
        <label className="text-sm font-medium text-white/70">
          Pipeline Execution
        </label>

        <div className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            disabled={isLoading}
            className={cn(
              "w-full p-3 rounded-lg border transition-colors",
              "bg-gray-800/50 border-gray-600/50 text-white",
              "hover:bg-gray-700/50 hover:border-gray-500/50",
              "focus:outline-none focus:ring-2 focus:ring-blue-500/50",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              "flex items-center justify-between"
            )}
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {isLoading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span className="text-sm text-gray-400">Loading executions...</span>
                </>
              ) : selectedExecution ? (
                <>
                  {getStatusIcon(selectedExecution.status)}
                  <div className="min-w-0 flex-1 text-left">
                    <div className="text-sm font-medium truncate">
                      {formatExecutionName(selectedExecution).primary}
                    </div>
                    <div className="text-xs text-gray-400 truncate">
                      {formatExecutionName(selectedExecution).secondary}
                    </div>
                  </div>
                </>
              ) : (
                <span className="text-sm text-gray-400">No execution selected</span>
              )}
            </div>

            <ChevronDown
              className={cn(
                "w-4 h-4 transition-transform text-gray-400",
                isOpen && "rotate-180"
              )}
            />
          </button>

          {isOpen && !isLoading && (
            <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600/50 rounded-lg shadow-xl max-h-80 overflow-auto">
              {executions.length === 0 ? (
                <div className="p-3 text-sm text-gray-400 text-center">
                  No executions found
                </div>
              ) : (
                <div className="py-1">
                  {executions.map((execution) => {
                    const { primary, secondary } = formatExecutionName(execution);
                    const isSelected = execution.id === currentExecutionId;

                    return (
                      <button
                        key={execution.id}
                        onClick={() => {
                          onExecutionChange(execution.id);
                          setIsOpen(false);
                        }}
                        className={cn(
                          "w-full p-3 text-left hover:bg-gray-700/50 transition-colors",
                          "flex items-center gap-3",
                          isSelected && "bg-blue-500/20 border-r-2 border-blue-500"
                        )}
                      >
                        {getStatusIcon(execution.status)}

                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-white truncate">
                            {primary}
                          </div>
                          <div className="text-xs text-gray-400 truncate">
                            {secondary}
                          </div>
                        </div>

                        <div className={cn(
                          "px-2 py-1 rounded text-xs font-medium border",
                          getStatusColor(execution.status)
                        )}>
                          {execution.status}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Close dropdown when clicking outside */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}